import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RippBadge, RippMetric } from "../v2/primitives";
import { api } from "../../lib/api";

const MODES = ["DISABLED", "LIVE_PROBE", "LIVE"];
const TONE = { DISABLED: "red", LIVE_PROBE: "cyan", LIVE: "emerald" };
const LABEL = (s) => s.replace(/_/g, " ");

export const CapitalRegistry = ({ status }) => {
  const [modes, setModes] = useState(null);
  const [risk, setRisk] = useState(null);
  const load = useCallback(async () => {
    try { const [m, r] = await Promise.all([api.meme.strategyModes(), api.meme.risk()]); setModes(m.modes); setRisk(r); } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, [load]);
  const set = async (strategy, mode) => {
    try { const r = await api.meme.setStrategyMode(strategy, mode); setModes(r.modes); toast.success(`${LABEL(strategy)} → ${LABEL(mode)}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "registry update failed"); }
  };
  const flags = risk?.flags || status?.flags || {};
  const perf = status?.perf || {};
  return (
    <div className="space-y-2 border border-[var(--r2-border)] rounded-sm p-2" data-testid="capital-registry">
      <div className="flex items-center justify-between">
        <div className="text-[8px] font-mono uppercase tracking-[0.18em] text-[var(--r2-text2)]">Capital registry · the only gate that deploys SOL · V3 phase 0</div>
        <div className="flex gap-1">{Object.entries(flags).map(([k, v]) => <RippBadge key={k} tone={v ? "emerald" : "red"} testid={`flag-${k}`}>{k.replace("_v2", "").replace(/_/g, " ")} {v ? "ON" : "OFF"}</RippBadge>)}</div>
      </div>
      <div className="grid grid-cols-3 gap-1" data-testid="registry-modes">
        {Object.entries(modes || {}).map(([s, m]) => (
          <div key={s} className="flex items-center justify-between gap-1 px-1.5 py-1 bg-white/[0.02] rounded-sm" data-testid={`registry-row-${s}`}>
            <span className="text-[8.5px] font-mono">{LABEL(s)}</span>
            <select value={m} onChange={(e) => set(s, e.target.value)} data-testid={`registry-select-${s}`}
              className="bg-transparent text-[8px] font-mono border border-[var(--r2-border)] rounded-sm px-1 py-0.5" style={{ color: `var(--r2-${TONE[m]})` }}>
              {MODES.map((o) => <option key={o} value={o} className="bg-black">{LABEL(o)}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-6 gap-2" data-testid="risk-daemon">
        <RippMetric k="Risk daemon" v={risk ? `${risk.enabled ? "ON" : "OFF"} · ${risk.interval_sec}s · ${risk.ticks} ticks` : "—"} small testid="risk-daemon-state" />
        <RippMetric k="Trading ready" v={risk ? (risk.trading_ready ? "READY" : risk.leader ? "WARM-UP" : "FOLLOWER") : "—"} tone={risk?.trading_ready ? "emerald" : "amber"} small testid="risk-trading-ready" />
        <RippMetric k="Daemon exits · TP locks" v={risk ? `${risk.exits} · ${risk.partials}` : "—"} small testid="risk-actions" />
        <RippMetric k="MFE capture" v={perf.mfe_capture_pct == null ? "—" : `${perf.mfe_capture_pct}%`} small testid="perf-mfe-capture" />
        <RippMetric k="TP1 capture" v={perf.tp1_capture_rate == null ? "—" : `${perf.tp1_capture_rate}% of ${perf.tp1_reached}`} small testid="perf-tp1-capture" />
        <RippMetric k="Stop slippage · rug loss" v={`${perf.stop_slippage_pct == null ? "—" : `${perf.stop_slippage_pct}%`} · $${perf.rug_loss_usd ?? "—"}`} small testid="perf-stop-slippage" />
      </div>
      {risk?.last_action && <div className="text-[8px] font-mono text-[var(--r2-text2)]" data-testid="risk-last-action">last daemon action: {risk.last_action}</div>}
    </div>
  );
};
